import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Makes a fixed-position panel draggable by a grip/handle. Pointer + touch.
 * Position RESETS to the default (anchored) spot every time `active` flips
 * from false -> true, so a popup always re-appears in the same place and it's
 * up to the user to move it — matches product spec.
 *
 * Usage:
 *   const { style, gripHandlers } = useDraggable(open, { width: 300, height: 160 });
 *   <div className="fixed z-[9999]" style={style}> ... <Header {...gripHandlers} /> </div>
 */
export function useDraggable(active, { width = 300, height = 160, margin = 16, bottomGap = 16 } = {}) {
  const [pos, setPos] = useState(null); // null = default anchored bottom-right
  const dragRef = useRef(null);
  const wasActive = useRef(false);

  useEffect(() => {
    if (active && !wasActive.current) setPos(null); // reset on each fresh open
    wasActive.current = active;
  }, [active]);

  const onDown = useCallback((e) => {
    // ignore drags that start on a button (e.g. the close X)
    if (e.target?.closest?.("button")) return;
    e.preventDefault?.();
    const pt = e.touches ? e.touches[0] : e;
    const base = pos || {
      x: window.innerWidth - width - margin,
      y: window.innerHeight - height - bottomGap,
    };
    dragRef.current = { startX: pt.clientX, startY: pt.clientY, baseX: base.x, baseY: base.y };
    const move = (ev) => {
      const p = ev.touches ? ev.touches[0] : ev;
      const d = dragRef.current; if (!d) return;
      let x = d.baseX + (p.clientX - d.startX);
      let y = d.baseY + (p.clientY - d.startY);
      x = Math.max(6, Math.min(window.innerWidth - 80, x));
      y = Math.max(6, Math.min(window.innerHeight - 48, y));
      setPos({ x, y });
      if (ev.cancelable) ev.preventDefault();
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
  }, [pos, width, height, margin, bottomGap]);

  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : { right: margin, bottom: bottomGap };

  const gripHandlers = { onPointerDown: onDown, onTouchStart: onDown, style: { touchAction: "none", cursor: "grab" } };

  return { pos, style, gripHandlers };
}

export default useDraggable;
